import { HORIZON_LABEL, type Horizon, type Mode, type Timeframe } from "./rounds";

/**
 * Share card (spec §16, GRA-82): a compact, Wordle-style text result a player
 * can paste anywhere. Text only in v1 — no image rendering.
 */

export const SHARE_URL = "candlealpha.com";

/**
 * Human-readable label for the active loop (ADR-0006): Predict names its
 * Timeframe and its guess horizon (`Predict · daily · trend`,
 * `Predict · monthly · next candle`); Trade and the Daily challenge have
 * neither (the challenge fixes both, so naming them would say nothing).
 */
export function shareModeLabel(
  mode: Mode,
  timeframe: Timeframe,
  horizon: Horizon,
): string {
  if (mode === "challenge") return "Daily challenge";
  if (mode !== "predict") return "Trade";
  return `Predict · ${timeframe} · ${HORIZON_LABEL[horizon]}`;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1);
}

/**
 * The header score readout's scope label (GRA-128), e.g. "Trend · Daily":
 * horizon before timeframe, so switching either one visibly changes the label
 * and a reset-looking score reads as a different scoreboard, not a flaky one.
 */
export function scoreScopeLabel(timeframe: Timeframe, horizon: Horizon): string {
  return `${capitalize(HORIZON_LABEL[horizon])} · ${capitalize(timeframe)}`;
}

/**
 * Build the shareable result string from the current run, e.g.
 * `candlealpha Predict · daily 🔥12 · 8/10 · candlealpha.com`: the active
 * Loop's label (spec §16, see state/loops.ts), best Streak, hit rate, link.
 */
export function buildShareText(opts: {
  label: string;
  correct: number;
  played: number;
  bestStreak: number;
}): string {
  return `candlealpha ${opts.label} 🔥${opts.bestStreak} · ${opts.correct}/${opts.played} · ${SHARE_URL}`;
}

/**
 * Copy `text` to the clipboard, resolving to whether it succeeded. Prefers the
 * async Clipboard API and falls back to a hidden-textarea `execCommand` when it
 * is unavailable (older/insecure contexts); never throws.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
